import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { downloadBlob } from "@/utils/download";

// Returns YYYY-MM-DD in the browser's local timezone (avoids UTC drift near midnight).
const localToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const localMonth = () => localToday().slice(0, 7);

const emptyForm = {
  supplier_id: "",
  supplier_name: "",
  invoice_number: "",
  category: "outros",
  date: localToday(),
  due_date: "",
  status: "pending",
  notes: "",
  declared_total: "",
};

const emptyItem = { name: "", quantity: 1, price: 0 };

export function useFaturas() {
  const { authAxios } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const abortRef = useRef(null);
  const [bills, setBills] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);

  // Two explicit display modes:
  //   MODO 1 — month (default): shows only selected month.
  //   MODO 2 — viewAll: shows all months (explicit "Ver tudo" button).
  // Changing the month picker always exits "Ver tudo" mode.
  const [month, setMonthRaw] = useState(localMonth);
  const [viewAll, setViewAll] = useState(false);

  const setMonth = useCallback((newMonth) => {
    setMonthRaw(newMonth);
    setViewAll(false); // changing month always exits "ver tudo"
  }, []);

  const toggleViewAll = useCallback(() => setViewAll(v => !v), []);
  const [showNew, setShowNew] = useState(false);
  const [editBill, setEditBill] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [showDetail, setShowDetail] = useState(null);
  const [form, setForm] = useState(() => ({ ...emptyForm, date: localToday() }));
  const [items, setItems] = useState([{ ...emptyItem }]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);

  const runDebug = useCallback(async () => {
    try {
      const [dbRes, dupRes] = await Promise.allSettled([
        authAxiosRef.current.get("/financiero/bills/debug"),
        authAxiosRef.current.get("/financiero/bills/duplicates"),
      ]);
      const d = dbRes.status === "fulfilled" ? dbRes.value.data : {};
      const dup = dupRes.status === "fulfilled" ? dupRes.value.data : {};
      const msg = [
        `restaurant_id: ${d.restaurant_id_from_token}`,
        `Faturas neste restaurante: ${d.bills_for_this_restaurant}`,
        `Faturas total na coleção: ${d.bills_in_entire_collection}`,
        `Últimas 5:`,
        ...(d["5_most_recent"] || []).map(b =>
          `  • ${b.supplier_name} | ${b.date} | ${b.total}€ | ${b.status} | rid=${b.restaurant_id}`
        ),
        "",
        `=== POSSÍVEIS DUPLICADAS ===`,
        `Total grupos duplicados: ${(dup.groups || []).length}  |  Entradas extra: ${dup.total_potential_duplicates || 0}`,
        ...(dup.groups || []).slice(0, 8).map(g =>
          `  • ${g.supplier} | ${g.date} | ${g.total}€ — ${g.count}x`
        ),
      ].join("\n");
      alert(msg);
    } catch (e) {
      alert("Erro no diagnóstico: " + (e?.response?.data?.detail || e.message));
    }
  }, []);

  const fetchData = useCallback(async () => {
    // Cancel any in-flight request before firing a new one (race condition protection)
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    // MODO 1 → month filter; MODO 2 → all_months=true (never omit both — backend defaults to current month)
    const billsUrl = viewAll
      ? "/financiero/bills?all_months=true"
      : `/financiero/bills?month=${month}`;
    try {
      const [bRes, sRes, cRes] = await Promise.allSettled([
        authAxiosRef.current.get(billsUrl, { signal: controller.signal }),
        authAxiosRef.current.get("/financiero/suppliers", { signal: controller.signal }),
        authAxiosRef.current.get("/financiero/bills/categories", { signal: controller.signal }),
      ]);
      if (controller.signal.aborted) return; // stale response — discard
      if (bRes.status === "fulfilled") setBills(bRes.value.data);
      if (sRes.status === "fulfilled") setSuppliers(sRes.value.data);
      if (cRes.status === "fulfilled") setCategories(cRes.value.data);
    } catch {} finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, viewAll]); // refetch when month or mode changes

  useEffect(() => { fetchData(); }, [fetchData]);

  const addItem = () => setItems([...items, { ...emptyItem }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const updateItem = (i, field, val) => {
    const n = [...items];
    n[i][field] = val;
    setItems(n);
  };
  const calcTotal = items.reduce((sum, it) => sum + (it.quantity * it.price), 0);

  const openEdit = (bill) => {
    setForm({
      supplier_id: bill.supplier_id || "",
      supplier_name: bill.supplier_name || "",
      invoice_number: bill.invoice_number || "",
      category: bill.category || "outros",
      date: bill.date || "",
      due_date: bill.due_date || "",
      status: bill.status || "pending",
      notes: bill.notes || "",
      declared_total: bill.declared_total ? String(bill.declared_total) : "",
    });
    setItems(bill.items?.length > 0 ? bill.items.map(i => ({ ...i })) : [{ ...emptyItem }]);
    setEditBill(bill);
    setShowNew(true);
  };

  // Returns true when the AI-extracted bill date is >12 months away from the selected month.
  // Used to warn the user before creating an invoice with a suspicious date.
  const isDateSuspicious = useMemo(() => (dateStr) => {
    if (!dateStr || viewAll) return false;
    try {
      const billDate = new Date(dateStr + "T12:00:00"); // noon avoids TZ boundary issues
      const [y, m_] = month.split("-").map(Number);
      const selectedDate = new Date(y, m_ - 1, 1);
      const diffMonths = Math.abs(
        (billDate.getFullYear() - selectedDate.getFullYear()) * 12 +
        (billDate.getMonth() - selectedDate.getMonth())
      );
      return diffMonths > 12;
    } catch {
      return false;
    }
  }, [month, viewAll]);

  const resetForm = () => {
    const today = localToday();
    const defaultDate = today.startsWith(month) ? today : `${month}-01`;
    setForm({ ...emptyForm, date: defaultDate });
    setItems([{ ...emptyItem }]);
    setEditBill(null);
    setShowNew(false);
  };

  const handleSubmit = async () => {
    if (!form.supplier_name && !form.supplier_id) { toast.error("Selecione um fornecedor"); return; }
    try {
      const payload = { ...form, items, declared_total: parseFloat(form.declared_total) || calcTotal };
      if (editBill) {
        await authAxios.put(`/financiero/bills/${editBill.id}`, payload);
        toast.success("Fatura atualizada!");
      } else {
        const res = await authAxios.post("/financiero/bills", payload);
        const d = res.data;
        toast.success("Fatura lançada!");
        if (d.supplier_created) toast.success(`Fornecedor "${d.supplier_name}" registado automaticamente`);
        if (d.stock_items_created > 0) toast.success(`${d.stock_items_created} ingrediente(s) criado(s) no inventário`);
        if (d.stock_entries_created > 0) toast.success(`Stock atualizado: ${d.stock_entries_created} produto(s) com entrada registada`);
      }
      resetForm();
      fetchData();
    } catch {
      toast.error("Erro ao salvar");
    }
  };

  const markPaid = async (id) => {
    try {
      await authAxios.patch(`/financiero/bills/${id}/pay`);
      toast.success("Marcada como paga");
      fetchData();
    } catch {
      toast.error("Erro");
    }
  };

  const deleteBill = async (id) => {
    if (!window.confirm("Tem certeza que deseja eliminar esta fatura?")) return;
    try {
      await authAxios.delete(`/financiero/bills/${id}`);
      toast.success("Eliminada");
      fetchData();
    } catch {
      toast.error("Erro");
    }
  };

  const handleAiUpload = async (file) => {
    if (!file) return;
    setAiLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await authAxios.post("/financiero/bills/extract-ai", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,   // 90s — AI processing can be slow; bypass the 30s global timeout
        _noRetry: true,   // signal interceptor: don't retry multipart uploads
      });
      if (res.data.success) {
        const docs = (res.data.documents || []).filter(Boolean);
        if (docs.length === 0) {
          toast.error("Nenhuma fatura encontrada no ficheiro. Tente uma foto mais nítida.");
          return;
        }
        setAiResult(docs[0]);
        if (docs.length > 1) toast.info(`${docs.length} faturas detetadas — use "Lote IA" para importar todas`);
      } else {
        toast.error(res.data.error || "Erro ao processar fatura", { duration: 6000 });
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.response?.data?.error || "";
      toast.error(msg ? `Erro: ${msg}` : "Erro ao analisar fatura com IA. Verifique a qualidade da imagem.", { duration: 6000 });
    } finally {
      setAiLoading(false);
    }
  };

  const applyAiResult = (extracted, updateCosts) => {
    setForm({
      supplier_id: "",
      supplier_name: extracted.supplier_name || "",
      invoice_number: extracted.invoice_number || "",
      category: extracted.category || "outros",
      date: extracted.date || localToday(),
      due_date: extracted.due_date || "",
      status: "pending",
      notes: extracted.notes || "",
      declared_total: extracted.total ? String(extracted.total) : "",
    });
    const billItems = (extracted.items || []).map(it => ({
      name: it.name || "",
      quantity: it.quantity || 1,
      price: it.price || 0,
      unit: it.unit || "",
      category: it.category || "",
    }));
    setItems(billItems.length > 0 ? billItems : [{ ...emptyItem }]);
    setAiResult(null);
    setShowNew(true);

    if (updateCosts) {
      const costUpdates = (extracted.items || [])
        .filter(it => it.stock_id && it.new_cost > 0)
        .map(it => ({ stock_id: it.stock_id, new_cost: it.new_cost }));
      if (costUpdates.length > 0) {
        authAxios.post("/financiero/bills/update-stock-costs", { updates: costUpdates })
          .then(() => toast.success(`Custos de ${costUpdates.length} ingrediente(s) atualizados nas fichas técnicas`))
          .catch(() => {});
      }
    }
  };

  const [aiCreating, setAiCreating] = useState(false);

  const createFromAi = async (extracted, updateCosts) => {
    setAiCreating(true);
    try {
      const billItems = (extracted.items || []).map(it => ({
        name: it.name || "",
        quantity: it.quantity || 1,
        price: it.price || 0,
        unit: it.unit || "",
        category: it.category || "",
      }));

      // Step 1: Create the bill — same endpoint as manual creation
      const res = await authAxiosRef.current.post("/financiero/bills", {
        supplier_name: extracted.supplier_name || "",
        invoice_number: extracted.invoice_number || "",
        category: extracted.category || "outros",
        date: extracted.date || localToday(),
        due_date: extracted.due_date || "",
        status: "pending",
        notes: extracted.notes || "",
        items: billItems,
        declared_total: extracted.total > 0 ? extracted.total : null,
      });
      const d = res.data;
      setAiResult(null);
      if (d.supplier_created) toast.success(`Fornecedor "${d.supplier_name}" registado automaticamente`);
      if (d.stock_items_created > 0) toast.success(`${d.stock_items_created} ficha(s) técnica(s) criada(s) automaticamente`);
      if (d.stock_entries_created > 0) toast.success(`Stock atualizado: ${d.stock_entries_created} produto(s) com entrada registada`);

      // Step 2: Update ingredient costs only after bill is confirmed created
      let costsUpdated = false;
      let costsFailed = false;
      if (updateCosts) {
        const costUpdates = (extracted.items || [])
          .filter(it => it.stock_id && it.new_cost > 0)
          .map(it => ({ stock_id: it.stock_id, new_cost: it.new_cost }));
        if (costUpdates.length > 0) {
          try {
            await authAxiosRef.current.post("/financiero/bills/update-stock-costs", { updates: costUpdates });
            costsUpdated = true;
          } catch {
            costsFailed = true;
          }
        }
      }

      // Step 3: Refetch with current month filter so state stays consistent
      await fetchData();

      // Step 4: Final toast
      if (costsUpdated) {
        const n = (extracted.items || []).filter(it => it.stock_id && it.new_cost > 0).length;
        toast.success(`Fatura criada e custos de ${n} ingrediente(s) atualizados`);
      } else if (costsFailed) {
        toast.success("Fatura criada. Alguns custos não foram atualizados.");
      } else {
        toast.success(`Fatura ${extracted.supplier_name || ""} criada com sucesso!`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao criar fatura");
    } finally {
      setAiCreating(false);
    }
  };

  const selectSupplier = (supplierId) => {
    const s = suppliers.find(sup => sup.id === supplierId);
    if (s) setForm({ ...form, supplier_id: s.id, supplier_name: s.name });
  };

  // Use declared_total (total real da nota c/ IVA) se preenchido; caso contrário usa o calculado dos itens
  const effectiveTotal = (b) => (b.declared_total > 0 ? b.declared_total : b.total) || 0;

  // bills already scoped to the selected month by the API — filter further by status/search.
  const _byStatus = filter === "all" ? bills : bills.filter(b => b.status === filter);
  const filteredBills = search.trim()
    ? _byStatus.filter(b => {
        const q = search.trim().toLowerCase();
        return (
          (b.supplier_name || "").toLowerCase().includes(q) ||
          (b.invoice_number || "").toLowerCase().includes(q) ||
          (categories[b.category] || b.category || "").toLowerCase().includes(q) ||
          (b.notes || "").toLowerCase().includes(q)
        );
      })
    : _byStatus;
  // Summary metrics derived from filteredBills so cards always match the visible list.
  const totalPending = filteredBills.filter(b => b.status === "pending").reduce((s, b) => s + effectiveTotal(b), 0);
  const totalPaid = filteredBills.filter(b => b.status === "paid").reduce((s, b) => s + effectiveTotal(b), 0);
  const totalAll = totalPending + totalPaid;
  const billsCount = filteredBills.length;

  // Debug aid — remove after production validation
  console.log("[FATURAS_DEBUG]", {
    modo: viewAll ? "all" : "month",
    mes: month,
    total_carregadas: bills.length,
    total_apos_filtro: filteredBills.length,
    soma_calculada: totalAll.toFixed(2),
    filtro_status: filter,
  });

  const today = localToday();
  const overdueBills = bills.filter(b => b.status === "pending" && b.due_date && b.due_date < today);
  const overdueTotal = overdueBills.reduce((s, b) => s + effectiveTotal(b), 0);

  const getDaysOverdue = (dueDate) => {
    const due = new Date(dueDate);
    const now = new Date();
    return Math.floor((now - due) / (1000 * 60 * 60 * 24));
  };

  const buildExportRows = (asNumbers) => filteredBills.map(b => ({
    Fornecedor: b.supplier_name || "",
    "Nº Factura": b.invoice_number || "",
    Categoria: categories[b.category] || b.category,
    Data: b.date,
    Vencimento: b.due_date || "",
    "Base s/ IVA": asNumbers ? b.total : b.total?.toFixed(2),
    IVA: asNumbers
      ? Math.max(0, effectiveTotal(b) - b.total)
      : Math.max(0, effectiveTotal(b) - b.total).toFixed(2),
    Total: asNumbers ? effectiveTotal(b) : effectiveTotal(b).toFixed(2),
    Status: b.status === "paid" ? "Pago" : "Pendente",
    Notas: b.notes || "",
  }));

  const exportCSV = () => {
    const ws = XLSX.utils.json_to_sheet(buildExportRows(false));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Faturas");
    const buf = XLSX.write(wb, { bookType: "csv", type: "array" });
    downloadBlob(new Blob([buf], { type: "text/csv;charset=utf-8" }), `faturas_${today}.csv`);
    toast.success("CSV exportado!");
  };

  const exportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(buildExportRows(true));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Faturas");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    downloadBlob(
      new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      `faturas_${today}.xlsx`
    );
    toast.success("Excel exportado!");
  };

  return {
    bills, suppliers, categories, loading,
    month, setMonth,
    viewAll, toggleViewAll,
    isDateSuspicious,
    billsCount,
    showNew, setShowNew,
    editBill,
    filter, setFilter,
    search, setSearch,
    showDetail, setShowDetail,
    form, setForm,
    items,
    calcTotal,
    filteredBills,
    totalPending, totalPaid, totalAll,
    overdueBills, overdueTotal,
    getDaysOverdue,
    addItem, removeItem, updateItem,
    openEdit, resetForm, handleSubmit,
    markPaid, deleteBill, selectSupplier,
    exportCSV, exportExcel,
    aiLoading, aiResult, setAiResult,
    handleAiUpload, applyAiResult, createFromAi, aiCreating,
    fetchData, runDebug,
  };
}
